import { createInjection } from '@/lib/createInjection';
import { TabFilterState } from '@/models/settouMessageBoard';
import { ComputedRef, computed } from '@vue/composition-api';
import { BoardTab } from '@/models/apis/settouMessageBoard/settouMessageBoardResponse';
import {
  BOARD_ROLE_CREATE,
  BOARD_ROLE_REPLY,
  BOARD_KYOKU_ROLES,
  departmentDispMap,
} from '@/components/SettouMessageBoard/consts/settou_message_board';
import { useStore } from '@/hooks/useStore';
import {
  JOHAISETSU_ROLE_EAST,
  JOHAISETSU_ROLE_KANAGAWA,
  JOHAISETSU_ROLE_WEST,
  JOHAISETSU_ROLE_HONSHA,
} from '@/consts/johaisetsu';
import { getBoardTabDefault } from '@/components/SettouMessageBoard/utils';
import { normalizeKey } from '@/lib/johaisetsu/johaisetsuHelper';

interface InjectedState {
  tabState: TabFilterState;
}
interface UseBoardDisplayControl {
  currentBoardTab: ComputedRef<BoardTab>;
  isHonbuBoard: ComputedRef<boolean>;
  isMaintenanceBoard: ComputedRef<boolean>;
  canUseHanSearchConditions: ComputedRef<boolean>;
  canUseHonbuReportSearchConditions: ComputedRef<boolean>;
  canUseCompanySearchConditions: ComputedRef<boolean>;
  boardTabDepartmentDisp: ComputedRef<string>;
  isCreatableRole: ComputedRef<boolean>;
  isRepliableRole: ComputedRef<boolean>;
  isUpperOrganization: ComputedRef<boolean>;
  canShowBulkDistribute: ComputedRef<boolean>;
  isHonshaUser: ComputedRef<boolean>;
  isKyokuUser: ComputedRef<boolean>;
}

const { provide, inject } = createInjection<InjectedState>('settouMessageBoard/useBoardDisplayControl');

export function provideBoardDisplayControl(tabState: TabFilterState): InjectedState {
  const injectedState = { tabState };
  provide(injectedState);
  return injectedState;
}

export default function useBoardDisplayControl(defaultInjectedState?: InjectedState): UseBoardDisplayControl {
  const injectedState = defaultInjectedState ?? inject();
  const tabState = injectedState.tabState;
  const store = useStore();
  const userState = store.state.user;

  const currentBoardTab = computed<BoardTab>(() => {
    return tabState.boardTabs.find(e =>
      e.id === tabState.currentTabId,
    ) ?? getBoardTabDefault();
  });
  const isHonbuBoard = computed<boolean>(() => {
    return currentBoardTab.value.board_kind === 'honbu';
  });
  const isMaintenanceBoard = computed<boolean>(() => {
    return currentBoardTab.value.board_kind === 'maintenance';
  });
  // ドライバーロール(xxx_driver)も局ロールと同じ扱いにすることがあるため、正規化したロールで判定する
  const normalizedJohaisetsuRole = computed<string>(() => normalizeKey(userState.johaisetsu_role));
  const isKyokuUser = computed<boolean>(() => {
    return normalizedJohaisetsuRole.value === JOHAISETSU_ROLE_WEST ||
      normalizedJohaisetsuRole.value === JOHAISETSU_ROLE_EAST ||
      normalizedJohaisetsuRole.value === JOHAISETSU_ROLE_KANAGAWA ||
      BOARD_KYOKU_ROLES.includes(normalizedJohaisetsuRole.value);
  });
  const isHonshaUser = computed<boolean>(() => {
    return normalizedJohaisetsuRole.value === JOHAISETSU_ROLE_HONSHA;
  });
  const canUseHanSearchConditions = computed<boolean>(() => {
    return isHonbuBoard.value;
  });
  const canUseHonbuReportSearchConditions = computed<boolean>(() => {
    return isHonbuBoard.value && isKyokuUser.value;
  });
  const canUseCompanySearchConditions = computed<boolean>(() => {
    return isMaintenanceBoard.value;
  });
  const boardTabDepartmentDisp = computed<string>(() => {
    return departmentDispMap[currentBoardTab.value.department];
  });
  const currentBoardRole = computed<number>(() => {
    return currentBoardTab.value.board_role;
  });
  const isCreatableRole = computed<boolean>(() => {
    return !userState.has_role_super_admin && currentBoardRole.value === BOARD_ROLE_CREATE;
  });
  const isRepliableRole = computed<boolean>(() => {
    return !userState.has_role_super_admin && currentBoardRole.value === BOARD_ROLE_REPLY;
  });
  const isUpperOrganization = computed<boolean>(() => {
    if (userState.has_role_super_admin) return false;

    return (isHonshaUser.value && isHonbuBoard.value) || (isKyokuUser.value && isMaintenanceBoard.value);
  });
  const canShowBulkDistribute = computed<boolean>(() => {
    return (isHonshaUser.value && isHonbuBoard.value) || (isKyokuUser.value && isMaintenanceBoard.value);
  });
  return {
    currentBoardTab,
    isHonbuBoard,
    isMaintenanceBoard,
    canUseHanSearchConditions,
    canUseHonbuReportSearchConditions,
    canUseCompanySearchConditions,
    boardTabDepartmentDisp,
    isCreatableRole,
    isRepliableRole,
    isUpperOrganization,
    canShowBulkDistribute,
    isHonshaUser,
    isKyokuUser,
  };
}
